import {uuid} from "@/interfaces";
import {useAppDispatch, useAppSelector} from "@/lib/store.ts";
import {useCallback, useEffect} from "react";
import {appendOlder, setLoading} from "@/app/user/messages/chat-rooms.slice.ts";
import {ChatMessagesRepository} from "@/repositories/chat-messages.repository.ts";
import {upsertUsers} from "@/lib/store/users.slice.ts";
import {realtime} from "@/lib/realtime.ts";

export function useChatMessages(roomId: uuid) {
    const roomState = useAppSelector((s) => s.chatRooms.byRoom[roomId]);
    const dispatch  = useAppDispatch();

    const fetchPage = useCallback(
        async (before: string | null) => {
            dispatch(setLoading({ roomId, loading: true }));

            const page = await ChatMessagesRepository.page(roomId, before);
            dispatch(upsertUsers(page.senders));

            dispatch(
                appendOlder({
                    roomId,
                    messages: page.messages,
                    fullyLoaded: !page.hasOlder,
                })
            );
        },
        [dispatch, roomId]
    );

    useEffect(() => {
        if (!roomState) fetchPage(null);
    }, [roomId, roomState, fetchPage]);

    useEffect(() => {
        realtime.openRoom(roomId);

        return () => {
            realtime.closeRoom();
        }
    }, [roomId]);

    const loadOlder = useCallback(async () => {
        if (roomState?.loading || roomState?.fullyLoaded) return;
        const before = roomState?.list.find((m) => !m.id.startsWith("tmp-"))?.id ?? null;
        await fetchPage(before);
    }, [roomState, fetchPage]);

    return {
        messages    : roomState?.list        ?? [],
        loadOlder,
        loading     : roomState?.loading     ?? false,
        fullyLoaded : roomState?.fullyLoaded ?? false,
    };
}